/**
 * Power Outlet Calculations Utility
 *
 * Provides comprehensive calculations and formatting for Smart Power Outlet devices.
 * Features:
 * - On/Off state control and monitoring
 * - Power consumption tracking (Watts, kW)
 * - Energy usage calculation (kWh)
 * - Cost calculation based on tariff rates
 * - Uptime and runtime tracking
 * - Schedule management (display only - control via API)
 * - Load monitoring (connected device power)
 * - Usage pattern analysis (24-hour breakdown)
 * - Multi-language support (Arabic/English)
 *
 * @version 2.4.0
 * @date January 23, 2026
 */

// ==================== Type Definitions ====================

/**
 * Power outlet state
 */
export type OutletState = 'on' | 'off' | 'scheduled' | 'error';

/**
 * Power outlet event from the device
 */
export interface OutletEvent {
  timestamp: Date;
  state: OutletState; // Current state
  powerW?: number; // Power consumption in Watts
  energyKwh?: number; // Cumulative energy in kWh
  voltage?: number; // Voltage in V
  current?: number; // Current in A
  triggeredBy?: 'manual' | 'schedule' | 'automation' | 'remote';
}

/**
 * Power outlet usage data for a specific hour
 */
export interface OutletHourPattern {
  hour: number; // Hour of day (0-23)
  onDuration: number; // Minutes the outlet was ON
  energyKwh: number; // Energy consumed in this hour
  averagePowerW: number; // Average power during ON time
  onOffCount: number; // Number of state changes
  isPeakHour: boolean; // True if this is peak usage hour
}

/**
 * Timeline item for outlet events
 */
export interface OutletTimelineItem {
  timestamp: Date;
  state: OutletState;
  powerW?: number;
  description: string;
  triggeredBy?: 'manual' | 'schedule' | 'automation' | 'remote';
}

/**
 * Schedule entry for outlet
 */
export interface OutletSchedule {
  id: string;
  name: string;
  enabled: boolean;
  action: 'on' | 'off';
  time: string; // Time in HH:mm format
  days: number[]; // Days of week (0=Sunday, 6=Saturday)
  repeat: boolean;
}

/**
 * Comprehensive outlet statistics
 */
export interface OutletStats {
  totalOnTime: number; // Total minutes ON
  totalOffTime: number; // Total minutes OFF
  totalEnergyKwh: number; // Total energy consumed
  totalCost: number; // Total cost
  averagePowerW: number; // Average power when ON
  peakPowerW: number; // Peak power
  onOffCycles: number; // Number of state changes
  utilizationRate: number; // Percentage of time ON (0-100)
  mostActiveHour: number; // Hour with most usage
  leastActiveHour: number; // Hour with least usage
}

/**
 * Real-time power outlet status
 */
export interface OutletStatus {
  currentState: OutletState;
  currentPowerW: number;
  isOn: boolean;
  lastStateChange?: Date;
  uptimeMinutes: number; // Time since last ON
  todayOnTime: number; // Total ON time today (minutes)
  todayEnergyKwh: number; // Today's energy consumption
  todayCost: number; // Today's cost
  yesterdayOnTime: number; // Yesterday's ON time
  yesterdayEnergyKwh: number; // Yesterday's energy
  yesterdayCost: number; // Yesterday's cost
  weekEnergyKwh: number; // This week's energy
  weekCost: number; // This week's cost
  monthEnergyKwh: number; // This month's energy
  monthCost: number; // This month's cost
  voltage?: number; // Current voltage
  current?: number; // Current amperage
  connectedLoad?: number; // Estimated connected load (W)
  nextScheduledAction?: {
    action: 'on' | 'off';
    time: Date;
  };
}

/**
 * Tariff rate structure (price per kWh)
 */
export interface TariffRate {
  pricePerKwh: number;
  currency: string;
  peakMultiplier?: number;
}

// ==================== Core Calculation Functions ====================

/**
 * Calculate total ON time in minutes for a time period
 *
 * @param events - Array of outlet events
 * @param startDate - Start of period (optional)
 * @param endDate - End of period (optional)
 * @returns Total minutes the outlet was ON
 */
export function calculateOnTime(events: OutletEvent[], startDate?: Date, endDate?: Date): number {
  if (!events || events.length === 0) return 0;

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  // Filter events within date range
  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end;
  });

  if (filteredEvents.length === 0) return 0;

  // Sort events by timestamp
  const sortedEvents = [...filteredEvents].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  let totalOnMinutes = 0;
  let lastOnTime: Date | null = null;

  for (let i = 0; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];

    if (event.state === 'on') {
      lastOnTime = event.timestamp;
    } else if (event.state === 'off' && lastOnTime) {
      const offTime = event.timestamp;
      const durationMs = offTime.getTime() - lastOnTime.getTime();
      totalOnMinutes += durationMs / (1000 * 60);
      lastOnTime = null;
    }
  }

  // If still ON at the end, count until end date
  if (lastOnTime) {
    const durationMs = end.getTime() - lastOnTime.getTime();
    totalOnMinutes += durationMs / (1000 * 60);
  }

  return Math.round(totalOnMinutes);
}

/**
 * Calculate energy consumption for outlet
 *
 * @param events - Array of outlet events
 * @param startDate - Start of period (optional)
 * @param endDate - End of period (optional)
 * @returns Total energy consumed in kWh
 */
export function calculateOutletEnergy(
  events: OutletEvent[],
  startDate?: Date,
  endDate?: Date,
): number {
  if (!events || events.length === 0) return 0;

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end;
  });

  if (filteredEvents.length === 0) return 0;

  // Method 1: If events have cumulative energyKwh
  const eventsWithEnergy = filteredEvents.filter((e) => e.energyKwh !== undefined);
  if (eventsWithEnergy.length >= 2) {
    const firstEvent = eventsWithEnergy[0];
    const lastEvent = eventsWithEnergy[eventsWithEnergy.length - 1];
    return Math.max(0, (lastEvent.energyKwh || 0) - (firstEvent.energyKwh || 0));
  }

  // Method 2: Calculate from power readings during ON periods
  const sortedEvents = [...filteredEvents].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  let totalEnergyKwh = 0;
  let lastOnEvent: OutletEvent | null = null;

  for (let i = 0; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];

    if (event.state === 'on') {
      lastOnEvent = event;
    } else if (event.state === 'off' && lastOnEvent && lastOnEvent.powerW) {
      const offTime = event.timestamp;
      const onTime = lastOnEvent.timestamp;
      const durationHours = (offTime.getTime() - onTime.getTime()) / (1000 * 60 * 60);
      const powerKw = lastOnEvent.powerW / 1000;
      totalEnergyKwh += powerKw * durationHours;
      lastOnEvent = null;
    }
  }

  return Math.round(totalEnergyKwh * 1000) / 1000;
}

/**
 * Count ON/OFF cycles in the time period
 *
 * @param events - Array of outlet events
 * @param startDate - Start of period (optional)
 * @param endDate - End of period (optional)
 * @returns Number of state changes
 */
export function countOnOffCycles(events: OutletEvent[], startDate?: Date, endDate?: Date): number {
  if (!events || events.length === 0) return 0;

  const now = new Date();
  const start = startDate || new Date(events[0].timestamp);
  const end = endDate || now;

  const filteredEvents = events.filter((event) => {
    const eventDate = new Date(event.timestamp);
    return eventDate >= start && eventDate <= end;
  });

  // Count state changes (transitions between on/off)
  let cycles = 0;
  let lastState: OutletState | null = null;

  for (const event of filteredEvents) {
    if (lastState !== null && event.state !== lastState) {
      if (event.state === 'on' || event.state === 'off') {
        cycles++;
      }
    }
    lastState = event.state;
  }

  return cycles;
}

/**
 * Generate 24-hour outlet usage pattern
 *
 * @param events - Array of outlet events
 * @param date - Specific date to analyze (optional, defaults to today)
 * @param tariffRate - Tariff rate for cost calculation
 * @returns Array of 24 hourly patterns
 */
export function getOutletPattern(
  events: OutletEvent[],
  date: Date = new Date(),
  tariffRate: TariffRate = { pricePerKwh: 0.18, currency: 'SAR' },
): OutletHourPattern[] {
  const patterns: OutletHourPattern[] = [];

  // Initialize all 24 hours
  for (let hour = 0; hour < 24; hour++) {
    patterns.push({
      hour,
      onDuration: 0,
      energyKwh: 0,
      averagePowerW: 0,
      onOffCount: 0,
      isPeakHour: false,
    });
  }

  if (!events || events.length === 0) return patterns;

  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  // Process each hour
  for (let hour = 0; hour < 24; hour++) {
    const hourStart = new Date(targetDate);
    hourStart.setHours(hour, 0, 0, 0);
    const hourEnd = new Date(targetDate);
    hourEnd.setHours(hour, 59, 59, 999);

    const hourEvents = events.filter((event) => {
      const eventDate = new Date(event.timestamp);
      return eventDate >= hourStart && eventDate <= hourEnd;
    });

    if (hourEvents.length > 0) {
      const onTime = calculateOnTime(events, hourStart, hourEnd);
      const energyKwh = calculateOutletEnergy(events, hourStart, hourEnd);
      const onOffCount = countOnOffCycles(events, hourStart, hourEnd);

      const powerReadings = hourEvents
        .filter((e) => e.state === 'on' && e.powerW)
        .map((e) => e.powerW!);
      const averagePowerW =
        powerReadings.length > 0
          ? Math.round(powerReadings.reduce((sum, p) => sum + p, 0) / powerReadings.length)
          : 0;

      patterns[hour] = {
        hour,
        onDuration: onTime,
        energyKwh,
        averagePowerW,
        onOffCount,
        isPeakHour: false,
      };
    }
  }

  // Identify peak hour (most ON time)
  const maxOnTime = Math.max(...patterns.map((p) => p.onDuration));
  if (maxOnTime > 0) {
    const peakHourPattern = patterns.find((p) => p.onDuration === maxOnTime);
    if (peakHourPattern) {
      peakHourPattern.isPeakHour = true;
    }
  }

  return patterns;
}

/**
 * Generate timeline of recent outlet events
 *
 * @param events - Array of outlet events
 * @param maxItems - Maximum number of items to return
 * @returns Array of timeline items
 */
export function getOutletTimeline(
  events: OutletEvent[],
  maxItems: number = 10,
): OutletTimelineItem[] {
  if (!events || events.length === 0) return [];

  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  const recentEvents = sortedEvents.slice(0, maxItems);

  return recentEvents.map((event) => {
    let description = event.state === 'on' ? 'Turned ON' : 'Turned OFF';

    if (event.triggeredBy) {
      const triggerText =
        event.triggeredBy === 'manual'
          ? 'Manually'
          : event.triggeredBy === 'schedule'
          ? 'By Schedule'
          : event.triggeredBy === 'automation'
          ? 'By Automation'
          : 'Remotely';
      description += ` (${triggerText})`;
    }

    if (event.powerW && event.state === 'on') {
      description += ` • ${(event.powerW / 1000).toFixed(2)} kW`;
    }

    return {
      timestamp: event.timestamp,
      state: event.state,
      powerW: event.powerW,
      description,
      triggeredBy: event.triggeredBy,
    };
  });
}

/**
 * Calculate comprehensive outlet statistics
 *
 * @param events - Array of outlet events
 * @param startDate - Start of period
 * @param endDate - End of period
 * @param tariffRate - Tariff rate for cost calculation
 * @returns Complete statistics object
 */
export function getOutletStatistics(
  events: OutletEvent[],
  startDate: Date,
  endDate: Date,
  tariffRate: TariffRate = { pricePerKwh: 0.18, currency: 'SAR' },
): OutletStats {
  if (!events || events.length === 0) {
    return {
      totalOnTime: 0,
      totalOffTime: 0,
      totalEnergyKwh: 0,
      totalCost: 0,
      averagePowerW: 0,
      peakPowerW: 0,
      onOffCycles: 0,
      utilizationRate: 0,
      mostActiveHour: 0,
      leastActiveHour: 0,
    };
  }

  const totalOnTime = calculateOnTime(events, startDate, endDate);
  const totalPeriodMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
  const totalOffTime = Math.max(0, totalPeriodMinutes - totalOnTime);

  const totalEnergyKwh = calculateOutletEnergy(events, startDate, endDate);
  const totalCost = totalEnergyKwh * tariffRate.pricePerKwh;

  const onEvents = events.filter((e) => e.state === 'on' && e.powerW);
  const averagePowerW =
    onEvents.length > 0
      ? Math.round(onEvents.reduce((sum, e) => sum + (e.powerW || 0), 0) / onEvents.length)
      : 0;

  const peakPowerW = onEvents.length > 0 ? Math.max(...onEvents.map((e) => e.powerW || 0)) : 0;

  const onOffCycles = countOnOffCycles(events, startDate, endDate);

  const utilizationRate =
    totalPeriodMinutes > 0 ? Math.round((totalOnTime / totalPeriodMinutes) * 100) : 0;

  const pattern = getOutletPattern(events, startDate, tariffRate);
  const mostActive = pattern.reduce((max, curr) => (curr.onDuration > max.onDuration ? curr : max));
  const leastActive = pattern.reduce((min, curr) =>
    curr.onDuration < min.onDuration && curr.onDuration > 0 ? curr : min,
  );

  return {
    totalOnTime,
    totalOffTime,
    totalEnergyKwh: Math.round(totalEnergyKwh * 1000) / 1000,
    totalCost: Math.round(totalCost * 100) / 100,
    averagePowerW,
    peakPowerW,
    onOffCycles,
    utilizationRate,
    mostActiveHour: mostActive.hour,
    leastActiveHour: leastActive.hour,
  };
}

/**
 * Get real-time power outlet status
 *
 * @param events - Array of outlet events
 * @param currentState - Current outlet state
 * @param currentPowerW - Current power reading (0 if OFF)
 * @param voltage - Current voltage (optional)
 * @param current - Current amperage (optional)
 * @param schedules - Array of schedules (optional)
 * @param tariffRate - Tariff rate for cost calculation
 * @returns Complete outlet status
 */
export function getOutletStatus(
  events: OutletEvent[],
  currentState: OutletState,
  currentPowerW: number,
  voltage?: number,
  current?: number,
  schedules?: OutletSchedule[],
  tariffRate: TariffRate = { pricePerKwh: 0.18, currency: 'SAR' },
): OutletStatus {
  const now = new Date();
  const isOn = currentState === 'on';

  // Find last state change
  const sortedEvents = [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  const lastStateChange = sortedEvents.find(
    (e) => e.state === 'on' || e.state === 'off',
  )?.timestamp;

  // Calculate uptime (time since last ON)
  let uptimeMinutes = 0;
  if (isOn && lastStateChange) {
    uptimeMinutes = Math.floor((now.getTime() - lastStateChange.getTime()) / (1000 * 60));
  }

  // Today's data
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const todayOnTime = calculateOnTime(events, todayStart, todayEnd);
  const todayEnergyKwh = calculateOutletEnergy(events, todayStart, todayEnd);
  const todayCost = todayEnergyKwh * tariffRate.pricePerKwh;

  // Yesterday's data
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const yesterdayEnd = new Date(todayStart);
  yesterdayEnd.setMilliseconds(-1);
  const yesterdayOnTime = calculateOnTime(events, yesterdayStart, yesterdayEnd);
  const yesterdayEnergyKwh = calculateOutletEnergy(events, yesterdayStart, yesterdayEnd);
  const yesterdayCost = yesterdayEnergyKwh * tariffRate.pricePerKwh;

  // This week's data
  const weekStart = new Date(now);
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  weekStart.setHours(0, 0, 0, 0);
  const weekEnergyKwh = calculateOutletEnergy(events, weekStart, now);
  const weekCost = weekEnergyKwh * tariffRate.pricePerKwh;

  // This month's data
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnergyKwh = calculateOutletEnergy(events, monthStart, now);
  const monthCost = monthEnergyKwh * tariffRate.pricePerKwh;

  // Estimate connected load (use recent ON power readings)
  const recentOnEvents = events.filter((e) => e.state === 'on' && e.powerW).slice(-10);
  const connectedLoad =
    recentOnEvents.length > 0
      ? Math.round(
          recentOnEvents.reduce((sum, e) => sum + (e.powerW || 0), 0) / recentOnEvents.length,
        )
      : currentPowerW;

  // Find next scheduled action
  let nextScheduledAction: { action: 'on' | 'off'; time: Date } | undefined;
  if (schedules && schedules.length > 0) {
    const enabledSchedules = schedules.filter((s) => s.enabled);
    // TODO: Implement schedule parsing and next action calculation
  }

  return {
    currentState,
    currentPowerW: Math.round(currentPowerW),
    isOn,
    lastStateChange,
    uptimeMinutes,
    todayOnTime,
    todayEnergyKwh: Math.round(todayEnergyKwh * 1000) / 1000,
    todayCost: Math.round(todayCost * 100) / 100,
    yesterdayOnTime,
    yesterdayEnergyKwh: Math.round(yesterdayEnergyKwh * 1000) / 1000,
    yesterdayCost: Math.round(yesterdayCost * 100) / 100,
    weekEnergyKwh: Math.round(weekEnergyKwh * 1000) / 1000,
    weekCost: Math.round(weekCost * 100) / 100,
    monthEnergyKwh: Math.round(monthEnergyKwh * 1000) / 1000,
    monthCost: Math.round(monthCost * 100) / 100,
    voltage,
    current,
    connectedLoad,
    nextScheduledAction,
  };
}

// ==================== Formatting Functions ====================

/**
 * Format outlet state text
 *
 * @param state - Outlet state
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized state text
 */
export function formatOutletState(state: OutletState, lang: 'ar' | 'en' = 'en'): string {
  const translations: { [key in OutletState]: { ar: string; en: string } } = {
    on: { ar: 'مشغّل', en: 'ON' },
    off: { ar: 'متوقف', en: 'OFF' },
    scheduled: { ar: 'مجدول', en: 'Scheduled' },
    error: { ar: 'خطأ', en: 'Error' },
  };

  return translations[state]?.[lang] || state;
}

/**
 * Format duration in minutes to readable format
 *
 * @param minutes - Duration in minutes
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted duration string
 */
export function formatDuration(minutes: number, lang: 'ar' | 'en' = 'en'): string {
  if (minutes < 60) {
    const formatted =
      lang === 'ar'
        ? Math.round(minutes)
            .toString()
            .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
        : Math.round(minutes).toString();
    return lang === 'ar' ? `${formatted} دقيقة` : `${formatted}m`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (lang === 'ar') {
    const hoursStr = hours.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    const minsStr = remainingMinutes.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    return `${hoursStr} س ${minsStr} د`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
}

/**
 * Format power value with unit
 *
 * @param powerW - Power in Watts
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted power string
 */
export function formatPowerOutlet(powerW: number, lang: 'ar' | 'en' = 'en'): string {
  if (powerW >= 1000) {
    const powerKw = powerW / 1000;
    const formatted =
      lang === 'ar'
        ? powerKw.toFixed(2).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
        : powerKw.toFixed(2);
    return lang === 'ar' ? `${formatted} ك.و` : `${formatted} kW`;
  } else {
    const formatted =
      lang === 'ar'
        ? Math.round(powerW)
            .toString()
            .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
        : Math.round(powerW).toString();
    return lang === 'ar' ? `${formatted} واط` : `${formatted} W`;
  }
}

/**
 * Format energy value
 *
 * @param energyKwh - Energy in kWh
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted energy string
 */
export function formatEnergyOutlet(energyKwh: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? energyKwh.toFixed(2).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : energyKwh.toFixed(2);
  return lang === 'ar' ? `${formatted} ك.و.س` : `${formatted} kWh`;
}

/**
 * Format cost with currency
 *
 * @param cost - Cost value
 * @param currency - Currency code
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted cost string
 */
export function formatCostOutlet(
  cost: number,
  currency: string = 'SAR',
  lang: 'ar' | 'en' = 'en',
): string {
  const formatted =
    lang === 'ar'
      ? cost.toFixed(2).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : cost.toFixed(2);

  if (currency === 'SAR') {
    return lang === 'ar' ? `${formatted} ريال` : `${formatted} SAR`;
  } else if (currency === 'USD') {
    return lang === 'ar' ? `${formatted} دولار` : `$${formatted}`;
  } else {
    return `${formatted} ${currency}`;
  }
}

/**
 * Format utilization rate percentage
 *
 * @param rate - Utilization rate (0-100)
 * @param lang - Language code ('ar' or 'en')
 * @returns Formatted percentage string
 */
export function formatUtilization(rate: number, lang: 'ar' | 'en' = 'en'): string {
  const formatted =
    lang === 'ar'
      ? Math.round(rate)
          .toString()
          .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])
      : Math.round(rate).toString();
  return lang === 'ar' ? `${formatted}٪` : `${formatted}%`;
}

/**
 * Get utilization level text
 *
 * @param rate - Utilization rate (0-100)
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized level text
 */
export function getUtilizationLevel(rate: number, lang: 'ar' | 'en' = 'en'): string {
  let level: 'very-high' | 'high' | 'medium' | 'low' | 'very-low';

  if (rate >= 80) level = 'very-high';
  else if (rate >= 60) level = 'high';
  else if (rate >= 40) level = 'medium';
  else if (rate >= 20) level = 'low';
  else level = 'very-low';

  const translations = {
    'very-high': { ar: 'مرتفع جداً', en: 'Very High' },
    high: { ar: 'مرتفع', en: 'High' },
    medium: { ar: 'متوسط', en: 'Medium' },
    low: { ar: 'منخفض', en: 'Low' },
    'very-low': { ar: 'منخفض جداً', en: 'Very Low' },
  };

  return translations[level][lang];
}

/**
 * Format trigger source text
 *
 * @param trigger - Trigger source
 * @param lang - Language code ('ar' or 'en')
 * @returns Localized trigger text
 */
export function formatTrigger(
  trigger: 'manual' | 'schedule' | 'automation' | 'remote',
  lang: 'ar' | 'en' = 'en',
): string {
  const translations = {
    manual: { ar: 'يدوي', en: 'Manual' },
    schedule: { ar: 'جدولة', en: 'Schedule' },
    automation: { ar: 'أتمتة', en: 'Automation' },
    remote: { ar: 'عن بُعد', en: 'Remote' },
  };

  return translations[trigger]?.[lang] || trigger;
}
